import { useState, useEffect, memo } from 'react';
import { motion } from 'framer-motion';
import { FiHeart, FiEye, FiPlus } from 'react-icons/fi';
import Image from 'next/image';
import CategoryFilter from './CategoryFilter';
import { getApiUrl, getUploadUrl } from '../utils/api';

const ProductGallery = ({ onOpenModal, onOpenProductModal }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [favorites, setFavorites] = useState(new Set());

  // Load favorites from localStorage on mount
  useEffect(() => {
    const savedFavorites = localStorage.getItem('productFavorites');
    if (savedFavorites) {
      try {
        const favoritesArray = JSON.parse(savedFavorites);
        setFavorites(new Set(favoritesArray));
      } catch (error) {
        console.error('Error loading favorites from localStorage:', error);
      }
    }
  }, []);

  // Save favorites to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('productFavorites', JSON.stringify([...favorites]));
  }, [favorites]);

  // Mock product data - in a real app, this would come from the API
  const mockProducts = [];

  useEffect(() => {
    console.log('[DEBUG] ProductGallery: selectedCategory changed to:', selectedCategory);
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const url = selectedCategory === 'all' ? getApiUrl('products') : getApiUrl(`products?category=${selectedCategory}`);
        console.log('[DEBUG] ProductGallery: Fetching from URL:', url);
        const response = await fetch(url);
        console.log('[DEBUG] ProductGallery: Response status:', response.status);
        if (response.ok) {
          const data = await response.json();
          console.log('[DEBUG] ProductGallery: Received products:', data.data.products?.length || 0);
          setProducts(data.data.products || []);
        } else {
          console.error('[DEBUG] ProductGallery: API failed, using mock data');
          // Fallback to mock data if API fails
          setProducts(mockProducts);
        }
      } catch (error) {
        console.error('Error fetching products:', error);
        // Fallback to mock data
        setProducts(mockProducts);
      }
      setLoading(false);
    };

    fetchProducts();
  }, [selectedCategory]);

  const filteredProducts = products;

  const toggleFavorite = (productId) => {
    const newFavorites = new Set(favorites);
    if (newFavorites.has(productId)) {
      newFavorites.delete(productId);
    } else {
      newFavorites.add(productId);
    }
    setFavorites(newFavorites);
  };

  const findOutMore = (product) => {
    onOpenProductModal(product);
  };

  if (loading) {
    return (
      <section className="py-24 bg-neutral-50">
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="spinner"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 bg-gradient-to-br from-neutral-50 to-primary-50/30">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-20 pt-[50px]"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-6">
            Our Collection
          </h2>
          <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
            Discover our curated selection of premium faux floral arrangements,
            perfect for making your special occasion truly memorable.
          </p>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <CategoryFilter
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
          />
        </motion.div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {filteredProducts.map((product, index) => (
            <motion.div
              key={product._id || product.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -8 }}
              className="group glass-card overflow-hidden transition-all duration-300"
            >
              {/* Product Image */}
              <div className="relative aspect-square overflow-hidden">
                <div className="w-full h-full bg-gradient-to-br from-neutral-100 to-neutral-200 border border-neutral-300 flex items-center justify-center">
                  {product.images && product.images.length > 0 ? (
                    (() => {
                      const image = product.images[0];
                      const imageUrl = typeof image === 'string' ? getUploadUrl(image) : getUploadUrl(image?.url) || '/images/placeholder.png';
                      const imageAlt = typeof image === 'string' ? product.name : image?.alt || product.name;
                      return imageUrl ? (
                        <Image
                          src={imageUrl}
                          alt={imageAlt}
                          fill
                          className="object-contain"
                          onError={(e) => console.error('[DEBUG] Image failed to load:', imageUrl, 'for product:', product.name)}
                        />
                      ) : (
                        <div className="text-6xl text-primary-300">🌸</div>
                      );
                    })()
                  ) : (
                    <div className="text-6xl text-primary-300">🌸</div>
                  )}
                </div>

                {/* Overlay with actions */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <div className="flex gap-3">
                    <button
                      onClick={() => toggleFavorite(product._id || product.id)}
                      className={`p-3 rounded-full backdrop-blur-sm transition-colors ${
                        favorites.has(product._id || product.id)
                          ? 'bg-red-500 text-white'
                          : 'bg-white/20 text-white hover:bg-white/30'
                      }`}
                    >
                      <FiHeart size={20} />
                    </button>
                    <button
                      onClick={() => findOutMore(product)}
                      className="p-3 rounded-full bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm"
                    >
                      <FiEye size={20} />
                    </button>
                  </div>
                </div>

                {/* Featured Badge */}
                {product.featured && (
                  <div className="absolute top-4 left-4 bg-primary-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                    Featured
                  </div>
                )}

              </div>

              {/* Product Info */}
              <div className="p-6">
                <div className="mb-2">
                  <span className="text-xs font-semibold text-primary-600 uppercase tracking-wide">
                    {typeof product.category === 'object' ? product.category?.name : product.category}
                  </span>
                </div>

                <h3 className="text-xl font-semibold text-neutral-800 mb-2 group-hover:text-primary-600 transition-colors">
                  {product.name}
                </h3>

                <p className="text-neutral-600 text-sm mb-4 line-clamp-2">
                  {product.description}
                </p>

                {/* Price Display or Quote Request */}
                <div className="mb-4">
                  {product.hasVariants ? (
                    // For products with variants, check if minimum price is 0
                    (() => {
                      const prices = product.variants?.map(v => v.price).filter(p => p != null) || [];
                      const minPrice = prices.length > 0 ? Math.min(...prices) : product.price || 0;
                      if (minPrice === 0) {
                        return null; // Hide price, show quote button below
                      }
                      return (
                        <>
                          <p className="text-lg font-semibold text-primary-600">
                            Starting from {product.formattedPrice || `$${minPrice.toFixed(2)}`}
                          </p>
                          {product.priceRange && (
                            <p className="text-sm text-neutral-500">
                              {product.priceRange.formatted}
                            </p>
                          )}
                        </>
                      );
                    })()
                  ) : (
                    // For products without variants
                    product.price === 0 ? null : (
                      <p className="text-lg font-semibold text-primary-600">
                        Prices from {product.formattedPrice || `$${product.price?.toFixed(2) || '0.00'}`}
                      </p>
                    )
                  )}
                </div>

                {/* Find Out More Button */}
                <button
                  onClick={() => findOutMore(product)}
                  className="w-full py-3 px-6 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 hover:shadow-lg hover:-translate-y-0.5 bg-primary-500 hover:bg-primary-600 text-white"
                >
                  Find Out More
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Load More / View All */}
        {filteredProducts.length >= 6 && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <button className="btn btn-outline text-lg px-8 py-4">
              View All Products
            </button>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default memo(ProductGallery);